import { useEffect, useMemo, useState } from "react";
import SurveyListHeader from "../../../../../component/common/layout/SurveyListHeader";
import SurveyListSidebar from "../../../../../component/common/layout/SurveyListSidebar";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import Svg from "../../../../../component/common/Svg";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import useVerifySurveyId from "../../../../../hooks/verifySurveyId";
import Select from "react-select";
import { toast } from "react-toastify";
import { getLeadGenVariable } from "../../../../../store/actions/variableAction";
import {
  getLeadErasureRules,
  addLeadErasureRule,
  updateLeadErasureRule,
  deleteLeadErasureRule,
  toggleLeadErasureRule,
} from "../../../../../store/actions/leadErasureAction";

const RULE_TYPES = [
  { value: "field_match", label: "Field match" },
  { value: "duplicate", label: "Duplicate" },
  { value: "utm_match", label: "UTM match" },
];

const FIELD_OPERATORS = [
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Does not equal" },
  { value: "contains", label: "Contains" },
  { value: "starts_with", label: "Starts with" },
  { value: "ends_with", label: "Ends with" },
  { value: "is_empty", label: "Is empty" },
  { value: "regex", label: "Matches regex" },
  { value: "length_equals", label: "Length equals" },
  { value: "length_not_equals", label: "Length does not equal" },
];

const UTM_FIELDS = [
  { value: "utm_source", label: "UTM source" },
  { value: "utm_medium", label: "UTM medium" },
  { value: "utm_campaign", label: "UTM campaign" },
];

const UTM_OPERATORS = [
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Does not equal" },
  { value: "contains", label: "Contains" },
  { value: "starts_with", label: "Starts with" },
  { value: "ends_with", label: "Ends with" },
  { value: "is_empty", label: "Is empty" },
  { value: "regex", label: "Matches regex" },
];

/** Preset id → regex source string saved in rule_config.value (same as custom regex). */
const REGEX_PRESETS = [
  { value: "custom", pattern: null },
  { value: "letters_only", pattern: String.raw`^[A-Za-z]+$` },
  { value: "digits_only", pattern: String.raw`^\d+$` },
  { value: "exactly_9_chars", pattern: String.raw`^.{9}$` },
  { value: "nine_same_character", pattern: String.raw`^(.)\1{8}$` },
  { value: "eight_plus_same_digits", pattern: String.raw`(\d)\1{7,}` },
];

function detectRegexPreset(storedValue) {
  if (storedValue === undefined || storedValue === null) return "custom";
  const s = String(storedValue);
  for (const p of REGEX_PRESETS) {
    if (p.value === "custom" || !p.pattern) continue;
    if (p.pattern === s) return p.value;
  }
  return "custom";
}

const KEEP_OPTIONS = [
  { value: "newest", label: "Keep newest" },
  { value: "oldest", label: "Keep oldest" },
];

const EMPTY_FORM = {
  rule_type: "field_match",
  field: "",
  operator: "equals",
  value: "",
  regex_preset: "custom",
  keep: "newest",
  is_active: true,
};

function buildRuleConfig(form) {
  switch (form.rule_type) {
    case "field_match":
      return { field: form.field, operator: form.operator, value: form.value };
    case "duplicate":
      return { field: form.field, keep: form.keep };
    case "utm_match":
      return { field: form.field, operator: form.operator, value: form.value };
    default:
      return {};
  }
}

function ruleDescription(rule, t) {
  const cfg = typeof rule.rule_config === "string"
    ? JSON.parse(rule.rule_config)
    : rule.rule_config;

  switch (rule.rule_type) {
    case "field_match":
      if (cfg.operator === "regex" && cfg.value !== undefined && t) {
        const preset = detectRegexPreset(cfg.value);
        if (preset !== "custom") {
          return `"${cfg.field}" → ${t(`survey.leadErasure.regex_preset.${preset}`)}`;
        }
        return `"${cfg.field}" matches regex "${cfg.value}"`;
      }
      return `"${cfg.field}" ${cfg.operator} "${cfg.value}"`;
    case "duplicate":
      return cfg.keep === "newest"
        ? `Duplicate "${cfg.field}" — archive older rows, keep newest submission`
        : `Duplicate "${cfg.field}" — archive new submission when older match exists`;
    case "utm_match":
      if (cfg.operator === "regex" && cfg.value !== undefined && t) {
        const preset = detectRegexPreset(cfg.value);
        if (preset !== "custom") {
          return `${cfg.field} → ${t(`survey.leadErasure.regex_preset.${preset}`)}`;
        }
        return `${cfg.field} matches regex "${cfg.value}"`;
      }
      return `${cfg.field} ${cfg.operator} "${cfg.value}"`;
    default:
      return rule.rule_type;
  }
}

function formFromRule(rule) {
  const cfg = typeof rule.rule_config === "string"
    ? JSON.parse(rule.rule_config)
    : rule.rule_config;

  const operator = cfg.operator || "equals";
  const val = cfg.value !== undefined ? cfg.value : "";
  return {
    rule_type: rule.rule_type,
    field: cfg.field || "",
    operator,
    value: val,
    regex_preset:
      operator === "regex" ? detectRegexPreset(val) : "custom",
    keep: cfg.keep || "newest",
    is_active: rule.is_active === 1,
  };
}

export default function LeadErasureRules() {
  const { t } = useTranslation();
  const survey_id = useVerifySurveyId();
  const dispatch = useDispatch();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editRuleId, setEditRuleId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const leadGenData = useSelector((state) => state?.variables?.leadGenData || []);
  const rules = useSelector((state) => state?.leadErasure?.rules || []);
  const loading = useSelector((state) => state?.leadErasure?.loading || false);
  const { survey_name } = useSelector((state) => state.permissionsData);

  const canMutate = editMode
    ? window.modulePermissions?.M23?.edit == 1
    : window.modulePermissions?.M23?.add == 1;

  const parseJsonSafe = (str, fallback = {}) => {
    if (!str) return fallback;
    if (typeof str === "object") return str;
    try { return JSON.parse(str); } catch { return fallback; }
  };

  const EXCLUDED_LEAD_FIELDS = ["HEADING", "DESC", "SUBMIT", "POSITION", "PRIVACY", "BANNER", "TANDC"];

  const regexPresetOptions = useMemo(
    () =>
      REGEX_PRESETS.map((p) => ({
        value: p.value,
        label: t(`survey.leadErasure.regex_preset.${p.value}`),
      })),
    [t]
  );

  const leadFieldOptions = (leadGenData || [])
    .filter((f) => {
      if (!f.su_name) return false;
      if (EXCLUDED_LEAD_FIELDS.includes(f.su_name)) return false;
      const fieldType = Number(f.su_form_variable_type);
      if (![1, 3, 4].includes(fieldType)) return false;
      if (fieldType === 1) {
        const options = parseJsonSafe(f.su_options, {});
        if (options?.["0"]?.type === "hidden") return false;
      }
      return true;
    })
    .map((f) => {
      const labels = parseJsonSafe(f.su_label_and_options, {});
      const label = labels?.["22"] || f.su_name;
      return { value: f.su_name, label };
    });

  useEffect(() => {
    if (survey_id) {
      dispatch(getLeadGenVariable(survey_id));
      dispatch(getLeadErasureRules(survey_id));
    }
  }, [survey_id, dispatch]);

  const openAddModal = () => {
    setForm(EMPTY_FORM);
    setEditMode(false);
    setEditRuleId(null);
    setIsModalOpen(true);
  };

  const openEditModal = (rule) => {
    setForm(formFromRule(rule));
    setEditMode(true);
    setEditRuleId(rule.rule_id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditMode(false);
    setEditRuleId(null);
    setForm(EMPTY_FORM);
  };

  const handleFieldChange = (key, val) => {
    if (!canMutate) return;
    setForm((prev) => {
      const updated = { ...prev, [key]: val };
      // Reset dependent fields when rule_type changes
      if (key === "rule_type") {
        updated.field = "";
        updated.operator = "equals";
        updated.value = "";
        updated.regex_preset = "custom";
        updated.keep = "newest";
      }
      if (key === "operator") {
        if (val !== "regex") {
          updated.regex_preset = "custom";
        } else if (prev.operator !== "regex") {
          updated.value = "";
          updated.regex_preset = "custom";
        }
      }
      return updated;
    });
  };

  const handleRegexPresetChange = (presetValue) => {
    if (!canMutate) return;
    const def = REGEX_PRESETS.find((p) => p.value === presetValue);
    setForm((prev) => ({
      ...prev,
      regex_preset: presetValue,
      value:
        presetValue === "custom"
          ? prev.value
          : def?.pattern ?? "",
    }));
  };

  const validateForm = () => {
    const { rule_type, field, operator, value } = form;
    if (rule_type === "field_match") {
      if (!field) return t("survey.leadErasure.errors.field_required");
      if (!operator) return t("survey.leadErasure.errors.operator_required");
      if (operator !== "is_empty" && !String(value).trim()) return t("survey.leadErasure.errors.value_required");
    } else if (rule_type === "duplicate") {
      if (!field) return t("survey.leadErasure.errors.field_required");
    } else if (rule_type === "utm_match") {
      if (!field) return t("survey.leadErasure.errors.field_required");
      if (!operator) return t("survey.leadErasure.errors.operator_required");
      if (operator !== "is_empty" && !String(value).trim()) return t("survey.leadErasure.errors.value_required");
    }
    if (
      (rule_type === "field_match" || rule_type === "utm_match") &&
      operator === "regex" &&
      form.regex_preset === "custom"
    ) {
      const v = String(value).trim();
      if (!v) return t("survey.leadErasure.errors.value_required");
      try {
        new RegExp(v);
      } catch (e) {
        return t("survey.leadErasure.errors.regex_invalid");
      }
    }
    return null;
  };

  const handleSubmit = () => {
    if (!canMutate) return;
    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    setSubmitting(true);
    const rule_config = buildRuleConfig(form);
    const payload = {
      su_survey_id: survey_id,
      rule_type: form.rule_type,
      rule_config,
      is_active: form.is_active ? 1 : 0,
    };

    if (editMode) {
      dispatch(
        updateLeadErasureRule({ ...payload, rule_id: editRuleId }, () => {
          toast.success(t("survey.leadErasure.updated"));
          setSubmitting(false);
          closeModal();
          dispatch(getLeadErasureRules(survey_id));
        })
      );
    } else {
      dispatch(
        addLeadErasureRule(payload, () => {
          toast.success(t("survey.leadErasure.created"));
          setSubmitting(false);
          closeModal();
          dispatch(getLeadErasureRules(survey_id));
        })
      );
    }
  };

  const handleToggle = (rule) => {
    const newStatus = rule.is_active === 1 ? 0 : 1;
    dispatch(
      toggleLeadErasureRule(
        { rule_id: rule.rule_id, su_survey_id: survey_id, is_active: newStatus },
        () => dispatch(getLeadErasureRules(survey_id))
      )
    );
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    dispatch(
      deleteLeadErasureRule(
        { rule_id: deleteTarget.rule_id, su_survey_id: survey_id },
        () => {
          toast.success(t("survey.leadErasure.deleted"));
          setDeleteTarget(null);
          dispatch(getLeadErasureRules(survey_id));
        }
      )
    );
  };

  const columns = [
    {
      name: t("survey.leadErasure.col_type"),
      selector: (row) => {
        const found = RULE_TYPES.find((r) => r.value === row.rule_type);
        return found ? found.label : row.rule_type;
      },
      sortable: true,
      width: "160px",
    },
    {
      name: t("survey.leadErasure.col_description"),
      selector: (row) => ruleDescription(row, t),
      wrap: true,
    },
    {
      name: t("survey.leadErasure.col_status"),
      cell: (row) => (
        <span
          className={"table-status " + (row.is_active ? "Active" : "Inactive")}
          style={window.modulePermissions?.M23?.edit === 1 ? { cursor: "pointer" } : {}}
          onClick={() => window.modulePermissions?.M23?.edit === 1 && handleToggle(row)}
          title={window.modulePermissions?.M23?.edit === 1 ? "Click to toggle" : ""}
        >
          {row.is_active ? "Active" : "Inactive"}
        </span>
      ),
      width: "120px",
    },
    {
      name: t("survey.leadErasure.col_actions"),
      cell: (row) => (
        <div className="d-flex action-wrapper">
          {window.modulePermissions?.M23?.edit === 1 && (
            <a
              href="#"
              className="action-btn"
              title={t("survey.leadErasure.edit")}
              onClick={(e) => { e.preventDefault(); openEditModal(row); }}
            >
              <Svg name="table-edit" />
            </a>
          )}
          {window.modulePermissions?.M23?.del === 1 && (
            <a
              href="#"
              className="action-btn"
              title={t("survey.leadErasure.delete")}
              onClick={(e) => { e.preventDefault(); setDeleteTarget(row); }}
            >
              <Svg name="table-delete" />
            </a>
          )}
        </div>
      ),
      width: "110px",
    },
  ];

  const needsField = form.rule_type === "field_match" || form.rule_type === "duplicate";
  const needsUtmField = form.rule_type === "utm_match";
  const needsOperatorAndValue = form.rule_type === "field_match" || form.rule_type === "utm_match";
  const needsKeep = form.rule_type === "duplicate";
  const showValueInput = needsOperatorAndValue && form.operator !== "is_empty";
  const operatorOptions = form.rule_type === "utm_match" ? UTM_OPERATORS : FIELD_OPERATORS;

  return (
    <div className="d-flex flex-column main-outer-wrapper main-site-wrapper bg-white">
      <SurveyListHeader />
      <div id="viewport" className="flex-grow-1 d-flex survey-main-wrapper bg-white">
        <SurveyListSidebar />
        <div className="flex-grow-1 page-content-wrap d-flex flex-column">
          <div className="page-heading d-flex align-items-center">
            <div className="flex-grow-1">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/admin/survey/list">{t("survey.title")}</Link>
                  </li>
                  {survey_name && (
                    <li className="breadcrumb-item">
                      <Link to={`/admin/survey/create/qa-builder?survey_id=${survey_id}`}>{survey_name}</Link>
                    </li>
                  )}
                  <li className="breadcrumb-item active" aria-current="page">
                    <strong>{t("survey.leadErasure.title")}</strong>
                  </li>
                </ol>
              </nav>
              <h1>{t("survey.leadErasure.title")}</h1>
            </div>
            {window.modulePermissions?.M23?.add === 1 && (
              <div className="page-heading-right">
                <button className="btn btn-primary add-btn" onClick={openAddModal}>
                  <Svg name="plus" /> {t("survey.leadErasure.add_rule")}
                </button>
              </div>
            )}
          </div>

          <div className="flex-grow-1 p-4">
            <div className="table-wrap custom-data-table">
              <DataTable
                columns={columns}
                data={rules}
                progressPending={loading}
                pagination
                paginationPerPage={10}
                noDataComponent={
                  <div className="no-data-wrap py-4 text-center">
                    <p className="mb-0">{t("survey.leadErasure.no_rules")}</p>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div className="modal-overlay-custom" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card modal-card-custom" style={{ width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto", borderRadius: 8 }}>
            <div className="card-header d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                {editMode ? t("survey.leadErasure.edit_rule") : t("survey.leadErasure.add_rule")}
              </h5>
              <button type="button" className="btn-close" onClick={closeModal} />
            </div>
            <div className="card-body">
              {/* Rule Type */}
              <div className="form-group mb-3">
                <label className="form-label-custom">{t("survey.leadErasure.rule_type")}</label>
                <Select
                  options={RULE_TYPES}
                  value={
                    RULE_TYPES.find((o) => o.value === form.rule_type) ??
                    (form.rule_type ? { value: form.rule_type, label: form.rule_type } : null)
                  }
                  onChange={(opt) => handleFieldChange("rule_type", opt.value)}
                  isDisabled={editMode || !canMutate}
                />
              </div>

              {/* Field selector (field_match / duplicate) */}
              {needsField && (
                <div className="form-group mb-3">
                  <label className="form-label-custom">{t("survey.leadErasure.field")}</label>
                  <Select
                    options={leadFieldOptions}
                    value={leadFieldOptions.find((o) => o.value === form.field) || null}
                    onChange={(opt) => handleFieldChange("field", opt ? opt.value : "")}
                    placeholder={t("survey.leadErasure.select_field")}
                    isClearable
                    isDisabled={!canMutate}
                  />
                </div>
              )}

              {/* UTM field selector */}
              {needsUtmField && (
                <div className="form-group mb-3">
                  <label className="form-label-custom">{t("survey.leadErasure.utm_field")}</label>
                  <Select
                    options={UTM_FIELDS}
                    value={UTM_FIELDS.find((o) => o.value === form.field) || null}
                    onChange={(opt) => handleFieldChange("field", opt ? opt.value : "")}
                    placeholder={t("survey.leadErasure.select_field")}
                    isClearable
                    isDisabled={!canMutate}
                  />
                </div>
              )}

              {/* Operator */}
              {needsOperatorAndValue && (
                <div className="form-group mb-3">
                  <label className="form-label-custom">{t("survey.leadErasure.operator")}</label>
                  <Select
                    options={operatorOptions}
                    value={operatorOptions.find((o) => o.value === form.operator) || null}
                    onChange={(opt) => handleFieldChange("operator", opt ? opt.value : "equals")}
                    isDisabled={!canMutate}
                  />
                </div>
              )}

              {/* Value input */}
              {showValueInput && form.operator === "regex" && (
                <div className="form-group mb-3">
                  <label className="form-label-custom">{t("survey.leadErasure.regex_pattern_label")}</label>
                  <Select
                    options={regexPresetOptions}
                    value={regexPresetOptions.find((o) => o.value === form.regex_preset) || regexPresetOptions[0]}
                    onChange={(opt) => handleRegexPresetChange(opt ? opt.value : "custom")}
                    isDisabled={!canMutate}
                  />
                  {form.regex_preset === "custom" && (
                    <>
                      <label className="form-label-custom mt-3">{t("survey.leadErasure.regex_custom_pattern")}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.value}
                        readOnly={!canMutate}
                        onChange={(e) => handleFieldChange("value", e.target.value)}
                        placeholder={t("survey.leadErasure.enter_regex_placeholder")}
                        autoComplete="off"
                      />
                      <small className="text-muted">{t("survey.leadErasure.regex_hint")}</small>
                    </>
                  )}
                </div>
              )}
              {showValueInput && form.operator !== "regex" && (
                <div className="form-group mb-3">
                  <label className="form-label-custom">{t("survey.leadErasure.value")}</label>
                  <input
                    type={form.operator === "length_equals" || form.operator === "length_not_equals" ? "number" : "text"}
                    className="form-control"
                    value={form.value}
                    readOnly={!canMutate}
                    onChange={(e) => handleFieldChange("value", e.target.value)}
                    placeholder={
                      form.operator === "starts_with"
                        ? t("survey.leadErasure.starts_with_hint")
                        : t("survey.leadErasure.enter_value")
                    }
                  />
                  {form.operator === "starts_with" && (
                    <small className="text-muted">{t("survey.leadErasure.starts_with_hint")}</small>
                  )}
                </div>
              )}

              {/* Keep strategy */}
              {needsKeep && (
                <div className="form-group mb-3">
                  <label className="form-label-custom">{t("survey.leadErasure.keep_strategy")}</label>
                  <Select
                    options={KEEP_OPTIONS}
                    value={KEEP_OPTIONS.find((o) => o.value === form.keep) || null}
                    onChange={(opt) => handleFieldChange("keep", opt ? opt.value : "newest")}
                    isDisabled={!canMutate}
                  />
                </div>
              )}

              {/* Active status */}
              <div className="form-group mb-3">
                <label className="form-label-custom">{t("survey.status")}</label>
                <div className="d-flex align-items-center flex-wrap form-check-list">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="ruleActive"
                      id="ruleStatusActive"
                      checked={form.is_active === true}
                      disabled={!canMutate}
                      onChange={() => handleFieldChange("is_active", true)}
                    />
                    <label className="form-check-label" htmlFor="ruleStatusActive">
                      {t("webhook.active")}
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="ruleActive"
                      id="ruleStatusInactive"
                      checked={form.is_active === false}
                      disabled={!canMutate}
                      onChange={() => handleFieldChange("is_active", false)}
                    />
                    <label className="form-check-label" htmlFor="ruleStatusInactive">
                      {t("webhook.inactive")}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-footer d-flex justify-content-end gap-2">
              <button className="btn btn-bordered" onClick={closeModal}>
                {t("survey.customVariable.cancel")}
              </button>
              <button
                className="btn btn-primary add-btn"
                onClick={handleSubmit}
                disabled={submitting || !canMutate}
              >
                <Svg name="plus" />{" "}
                {editMode ? t("survey.share.update") : t("survey.share.add")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation — fixed overlay + card (matches add/edit modal; avoids overlap with DataTable) */}
      {deleteTarget && (
        <div
          className="modal-overlay-custom"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lead-erasure-delete-title"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1060,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setDeleteTarget(null);
          }}
        >
          <div
            className="card modal-card-custom shadow"
            style={{
              width: "100%",
              maxWidth: 520,
              maxHeight: "90vh",
              overflowY: "auto",
              borderRadius: 8,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-body">
              <h5 id="lead-erasure-delete-title" className="mb-3">
                {t("survey.customVariable.confirm_delete")}
              </h5>
              <p
                className="mb-4"
                style={{
                  fontSize: 16,
                  lineHeight: 1.5,
                  color: "#4a5568",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {t("survey.customVariable.del1")}{" "}
                <strong style={{ color: "#0f2744" }}>
                  &ldquo;{ruleDescription(deleteTarget, t)}&rdquo;
                </strong>{" "}
                {t("survey.customVariable.permanently")}
              </p>
              <div className="d-flex justify-content-end gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-bordered"
                  onClick={() => setDeleteTarget(null)}
                >
                  {t("survey.customVariable.cancel")}
                </button>
                <button type="button" className="btn btn-delete" onClick={handleDeleteConfirm}>
                  {t("settings.roles.delete_action.delete")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
